import type { ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'

import {
  Checkbox,
  EmptyState,
  type ICheckboxProps,
  type IEmptyStateProps,
  type IWithChecklistPopoverProps,
  MenuList,
  MenuListItem,
  Space,
  SubText,
  Table,
  TableBanner,
  type TableBannerType,
  TableBodyAsRow,
  TableBodyAsRowFragment,
  WithChecklistPopover,
} from '@procurify/ui'
import { noop } from 'lodash'

import { TableLoader } from '../TableLoader'
import { DataTableStyled } from './styles/DataTableStyled'
import {
  type AdditionalCellWithSpan,
  type AdditionalRowCellType,
  type DatatableColumnConfig,
  type RowCellCheckboxProps,
  type RowCellMenuListItemProps,
  type DataTableAnalyticsIds,
} from './types/DataTable.types'

const MAX_COLUMN_COUNT = 99
const CHECKBOX_CELL_WIDTH = 60

export interface IDataTableProps<T> {
  id: string
  data: T[]
  'aria-label'?: string
  'aria-labelledby'?: string
  columns: DatatableColumnConfig<any, T>[]
  isLoading?: boolean
  loaderRows?: number
  indentAdditionalRowBorder?: number
  /**
   * @param data the data of the row
   * @param rowIndex the index of the row
   * @param columnIndexes an object containing all the columns which has a `key` and it's column index
   */
  renderAdditionalRows?: (
    data: T,
    rowIndex: number,
    columnIndexes: {
      [key: string]: number
    },
    numColumns: number
  ) => AdditionalRowCellType[][]
  renderHeaderBanner?: ReactNode
  headerBannerType?: TableBannerType
  customizableColumns?: boolean
  persistCustomizedColumns?: boolean
  zebra?: boolean
  hoverable?: boolean
  hideHeader?: boolean
  horizontalScroll?: boolean
  /**
   * Disable scroll by setting `overflow: visible` for all elements
   */
  disableScroll?: boolean
  rowHeight?: number
  /**
   * Callback when a table row is clicked
   */
  onRowClick?: (data: T, rowIndex: number) => void
  /**
   * Add a menulist at the end of each row with the specified items
   */
  menuListItems?: (data: T, rowIndex: number) => RowCellMenuListItemProps[]
  /**
   * The accessible name of each row's menu trigger. Pass a function of the row
   * so each trigger names its row (e.g. "Actions for {name}"); a string gives
   * every row's trigger the same name. Defaults to "Open menu list for row N".
   */
  menuListAriaLabel?: string | ((data: T, rowIndex: number) => string)
  stickyMenuList?: boolean
  /**
   * Add a checkbox to each table row
   */
  enableCheckbox?: boolean
  /**
   * Analytics IDs for the DataTable
   */
  analyticsIds?: DataTableAnalyticsIds
  /**
   * A function to return a boolean to determine each checkbox's state
   */
  isItemChecked?: (data: T, rowIndex: number) => boolean
  /**
   * State of the "select all" checkbox
   */
  isAllChecked?: boolean
  /**
   * Callback when "Select all" checkbox state changes
   */
  onCheckAll?: (checked: boolean) => void
  /**
   * Callback when a table row's checkbox state changes
   */
  onItemCheck?: (item: T, checked: boolean) => void
  /**
   * A function to return the aria-label for each row's checkbox
   */
  checkboxAriaLabel?: (data: T, rowIndex: number) => string
  decorateCheckbox?: (
    checkbox: ReactNode,
    metadata: {
      checkboxProps: RowCellCheckboxProps
      data: T
      rowIndex: number
    }
  ) => ReactNode
  /**
   * Function to determine if a table row's checkbox is disabled
   */
  isItemCheckboxDisabled?: (item: T, rowIndex: number) => boolean
  /**
   * Disabled state of all checkboxes. This overrides [isItemCheckboxDisabled]
   */
  isCheckboxDisabled?: boolean
  /**
   * Disabled state of the "Select all" checkbox
   */
  isCheckAllCheckboxDisabled?: boolean
  emptyStateText?: ReactNode
  emptyStateContent?: ReactNode
  emptyStateContext?: IEmptyStateProps['context']
  emptyStateSize?: 'small' | 'normal'
  /**
   * Function to determine if a table's row is selected - see `ITableRowProps['selected']`
   */
  isRowSelected?: (item: T, rowIndex: number) => boolean
}

interface CommonHelperArgs extends IDataTableProps<any> {
  activeColumns: DatatableColumnConfig[]
  checklist: {}
}

interface CommonRowHelperArgs extends CommonHelperArgs {
  rowData: any
  rowIndex: number
}

const getFilteredColumns = ({
  columns = [],
  checklist,
  customizableColumns,
  tableWidth,
}: {
  columns: DatatableColumnConfig[]
  checklist: {}
  customizableColumns: boolean
  tableWidth: number
}) => {
  return columns.filter((colConfig) => {
    return (
      colConfig &&
      !colConfig.disabled &&
      tableWidth >= (colConfig.minTableWidth || 0) &&
      (customizableColumns
        ? checklist[colConfig.label] || colConfig.locked
        : true)
    )
  })
}

const renderCell = (
  args: CommonRowHelperArgs & {
    key: string | number
    colConfig: DatatableColumnConfig
  }
) => {
  const { key, rowData, rowIndex, colConfig } = args

  let componentData
  if (typeof colConfig.getter === 'string') {
    componentData = rowData[colConfig.getter]
  } else {
    componentData = colConfig.getter(rowData, rowIndex)
  }

  return (
    <Table.Cell
      key={key}
      aria-label={`${colConfig.label} cell`}
      align={colConfig.textAlign}
      {...(args.rowHeight && { height: args.rowHeight })}
      width={colConfig.width}
      {...(colConfig.minWidth !== undefined &&
        colConfig.minWidth !== null && {
          minWidth: colConfig.minWidth,
        })}
      valign={colConfig.valign}
    >
      {colConfig.decorate
        ? colConfig.decorate(
            renderCellComponent(args),
            rowData,
            rowIndex,
            componentData
          )
        : renderCellComponent(args)}
    </Table.Cell>
  )
}

const renderCellComponent = ({
  rowData,
  rowIndex,
  colConfig,
  onRowClick,
}: CommonRowHelperArgs & {
  colConfig: DatatableColumnConfig
}) => {
  let componentData
  if (typeof colConfig.getter === 'string') {
    componentData = rowData[colConfig.getter]
  } else {
    componentData = colConfig.getter(rowData, rowIndex)
  }
  const CellComponent: DatatableColumnConfig['BodyCellComponent'] =
    colConfig.BodyCellComponent

  return onRowClick && colConfig.clickable ? (
    <div
      style={{
        display: 'inline-block',
        width: 'fit-content',
        // Fallback for older browsers that don't support fit-content
        maxWidth: '100%',
      }}
      onClick={(e) => {
        e.stopPropagation()
      }}
    >
      <CellComponent data={componentData} />
    </div>
  ) : (
    <CellComponent data={componentData} />
  )
}

const renderHeaderCell = (colConfig: DatatableColumnConfig) => {
  return (
    <>
      {colConfig.headerCheckbox && (
        <span className='label-checkbox'>
          <Checkbox
            onClick={() => colConfig.onHeaderCheck(!colConfig.headerChecked)}
            checked={colConfig.headerChecked}
            aria-label={`Check ${colConfig.label} header checkbox`}
          />
        </span>
      )}
      {colConfig.label}
      {colConfig.labelIcon && (
        <SubText inline className='label-icon'>
          {colConfig.labelIcon}
        </SubText>
      )}
    </>
  )
}

const renderHeaderColumns = ({
  activeColumns,
  checklistMenu,
  customizableColumns,
  menuListItems,
  stickyMenuList,
}: CommonHelperArgs & {
  checklistMenu: ReactNode
}) => {
  const needEndColumn = customizableColumns || !!menuListItems

  return (
    <>
      {activeColumns.map((colConfig, index) => (
        <Table.Cell
          key={`${colConfig.type}-${index}`}
          align={colConfig.textAlign}
          width={colConfig.width}
        >
          {colConfig.decorateHeader
            ? colConfig.decorateHeader(renderHeaderCell(colConfig))
            : renderHeaderCell(colConfig)}
        </Table.Cell>
      ))}

      {needEndColumn && (
        <Table.Cell
          width={1}
          align='center'
          {...(stickyMenuList && { stickyRight: true })}
        >
          {customizableColumns ? checklistMenu : null}
        </Table.Cell>
      )}
    </>
  )
}

const renderBodyColumns = (args: CommonRowHelperArgs) => {
  const {
    activeColumns,
    customizableColumns,
    menuListItems,
    menuListAriaLabel,
    stickyMenuList,
    rowData,
    rowIndex,
  } = args

  const menuItems = (menuListItems?.(rowData, rowIndex) || []).map(
    (menuItemProps, i) => {
      return (
        <MenuListItem
          key={`item ${i} for row ${rowIndex}`}
          {...menuItemProps}
        />
      )
    }
  )

  const hasMenuList = menuItems.length > 0
  const needEndColumn = customizableColumns || !!menuListItems
  const rowMenuListAriaLabel =
    typeof menuListAriaLabel === 'function'
      ? menuListAriaLabel(rowData, rowIndex)
      : menuListAriaLabel

  return (
    <>
      {activeColumns.map((colConfig, colIndex) =>
        renderCell({
          key: colIndex,
          colConfig,
          ...args,
        })
      )}
      {needEndColumn && (
        <Table.Cell
          width={1}
          onClick={(e) => {
            if (!hasMenuList) return

            e.stopPropagation()
          }}
          align='center'
          {...(stickyMenuList && { stickyRight: true })}
        >
          {hasMenuList ? (
            <MenuList
              ariaLabel={
                rowMenuListAriaLabel || `Open menu list for row ${rowIndex + 1}`
              }
            >
              {menuItems}
            </MenuList>
          ) : null}
        </Table.Cell>
      )}
    </>
  )
}

const renderLoaderColumn = ({
  activeColumns,
  loaderRows,
  enableCheckbox,
  menuListItems,
  rowHeight,
  zebra,
}: CommonHelperArgs) => {
  return (
    <TableLoader
      rows={loaderRows}
      rowHeight={rowHeight}
      zebra={zebra}
      widths={[
        ...(enableCheckbox ? [16] : []),
        ...activeColumns.map((colConfig) => {
          return {
            width: colConfig.loaderWidth,
            alignRight: colConfig.textAlign === 'right',
          }
        }),
        // Stands in for the row menu trigger, which sets a loaded row's height.
        ...(menuListItems ? ['button' as const] : []),
      ]}
    />
  )
}

const isCellWithSpan = (
  cell: AdditionalRowCellType
): cell is AdditionalCellWithSpan => {
  return (cell as AdditionalCellWithSpan).render !== undefined
}

const isCellColumnConfig = (
  cell: AdditionalRowCellType
): cell is DatatableColumnConfig => {
  return (cell as DatatableColumnConfig).BodyCellComponent !== undefined
}

const renderAdditionals = (args: CommonRowHelperArgs) => {
  const {
    renderAdditionalRows,
    rowData,
    rowIndex,
    activeColumns,
    customizableColumns,
    enableCheckbox,
    menuListItems,
  } = args

  if (!renderAdditionalRows) return null

  const maxColumns =
    activeColumns.length +
    (customizableColumns ? 1 : 0) +
    (enableCheckbox ? 1 : 0) +
    (menuListItems ? 1 : 0)

  const columnIndexes = activeColumns.reduce((prev, column, index) => {
    if (!column.key) return prev
    prev[column.key] = index + (enableCheckbox ? 1 : 0)
    return prev
  }, {})

  const rowConfigs = renderAdditionalRows(
    rowData,
    rowIndex,
    columnIndexes,
    maxColumns
  )

  if (!Array.isArray(rowConfigs) || rowConfigs.length < 1) return null

  const additionalRows = []

  rowConfigs.forEach((rowConfig, index) => {
    const cellsToRender = []

    let cellsRendered = 0

    rowConfig.forEach((cellData, cellIndex) => {
      if (cellsRendered >= maxColumns) return

      if (typeof cellData === 'number' && cellData > 0) {
        cellsToRender.push(<Table.Cell key={cellIndex} colSpan={cellData} />)
        cellsRendered += cellData
      } else if (isCellWithSpan(cellData)) {
        cellsToRender.push(
          <Table.Cell
            key={cellIndex}
            align={cellData.textAlign}
            colSpan={cellData.colSpan || 1}
          >
            {cellData.render}
          </Table.Cell>
        )
        cellsRendered += cellData.colSpan || 1
      } else if (isCellColumnConfig(cellData)) {
        cellsToRender.push(
          renderCell({
            key: cellIndex,
            colConfig: cellData,
            ...args,
          })
        )
        cellsRendered += 1
      } else if (cellData) {
        cellsToRender.push(<Table.Cell key={cellIndex}>{cellData}</Table.Cell>)
        cellsRendered += 1
      }
    })

    /**
     * If the define cells do not fill the rest of the columns,
     * add a filler cell to span the additional rows to the end
     */
    if (cellsRendered < maxColumns)
      cellsToRender.push(
        <Table.Cell key='fill' colSpan={maxColumns - cellsRendered} />
      )

    additionalRows.push(
      <TableBodyAsRowFragment key={index}>
        {cellsToRender}
      </TableBodyAsRowFragment>
    )
  })

  return additionalRows
}

const defaultDecorateCheckboxFn = (checkbox) => checkbox

/**
 * A config-driven data table built on the `@procurify/ui` `Table` primitive.
 * Describe each column with `col(type, { label, getter })` and DataTable handles
 * cell rendering and alignment, column customization, row selection, loading and
 * empty states, expandable rows, and responsive columns.
 */
export const DataTable = <DataType,>({
  id,
  analyticsIds = {},
  data = [],
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  columns = [],
  isLoading = false,
  loaderRows = 5,
  rowHeight,
  renderAdditionalRows,
  renderHeaderBanner,
  headerBannerType,
  indentAdditionalRowBorder = 0,
  customizableColumns = true,
  persistCustomizedColumns = false,
  zebra = true,
  hoverable = false,
  hideHeader = false,
  horizontalScroll = true,
  enableCheckbox = false,
  isAllChecked = false,
  onCheckAll,
  isItemChecked = () => false,
  onItemCheck = noop,
  checkboxAriaLabel = (_, rowIndex) => `Select row ${rowIndex + 1}`,
  decorateCheckbox = defaultDecorateCheckboxFn,
  isItemCheckboxDisabled = () => false,
  isCheckboxDisabled = false,
  isCheckAllCheckboxDisabled = false,
  emptyStateContext,
  emptyStateText,
  emptyStateContent,
  emptyStateSize,
  onRowClick,
  menuListItems,
  menuListAriaLabel,
  stickyMenuList,
  isRowSelected,
  disableScroll,
}: IDataTableProps<DataType>) => {
  const initialChecklist: IWithChecklistPopoverProps['checklist'] = columns
    .filter((colConfig) => !colConfig.disabled)
    .map((colConfig) => ({
      label: colConfig.label,
      value: colConfig.label,
      checked: colConfig.defaultVisible,
      locked: colConfig.locked,
    }))

  const showLoader = isLoading
  const showEmptyState = !isLoading && !data?.length
  const showData = !isLoading && data?.length > 0

  const renderRowCellCheckbox = (rowData: DataType, rowIndex: number) => {
    const decorateCheckboxFn =
      typeof decorateCheckbox === 'function'
        ? decorateCheckbox
        : defaultDecorateCheckboxFn

    const checkboxProps: ICheckboxProps = {
      inputProps: {
        'aria-label': checkboxAriaLabel(rowData, rowIndex),
      },
      checked: isItemChecked(rowData, rowIndex),
      disabled: isCheckboxDisabled || isItemCheckboxDisabled(rowData, rowIndex),
    }

    const CheckboxComponent = <Checkbox {...checkboxProps} />

    return decorateCheckboxFn(CheckboxComponent, {
      data: rowData,
      checkboxProps,
      rowIndex,
    })
  }

  const _onItemCheck = (rowData: DataType, rowIndex: number) => {
    if (isCheckboxDisabled || isItemCheckboxDisabled(rowData, rowIndex)) return

    onItemCheck(rowData, !isItemChecked(rowData, rowIndex))
  }

  const _onCheckAll = () => {
    if (isCheckAllCheckboxDisabled || isCheckboxDisabled) return

    onCheckAll(!isAllChecked)
  }

  const [tableWidth, setTableWidth] = useState(Infinity)
  const tableRef = useRef(null)

  useEffect(() => {
    if (tableRef.current) {
      const observer = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const newWidth = entry.contentRect.width
          if (newWidth > 0) setTableWidth(newWidth)
        }
      })

      observer.observe(tableRef.current)

      return () => {
        observer.disconnect()
      }
    }
  }, [])

  return (
    <WithChecklistPopover
      id={id}
      analyticsId={analyticsIds?.columnOptionsButton}
      checklist={initialChecklist}
      persist={persistCustomizedColumns}
      key={initialChecklist.length}
    >
      {({ checklistMenu, checklist }) => {
        const commonArgs: CommonHelperArgs = {
          id,
          analyticsIds,
          data,
          columns,
          isLoading,
          loaderRows,
          renderAdditionalRows,
          indentAdditionalRowBorder,
          customizableColumns,
          persistCustomizedColumns,
          hoverable,
          checklist,
          activeColumns: getFilteredColumns({
            checklist,
            columns,
            customizableColumns,
            tableWidth,
          }),
          enableCheckbox,
          checkboxAriaLabel,
          menuListItems,
          menuListAriaLabel,
          stickyMenuList,
          rowHeight,
          zebra,
          onRowClick,
        }

        return (
          <DataTableStyled
            indentAdditionalRow={indentAdditionalRowBorder}
            clickable={!!onRowClick}
            disableScroll={disableScroll}
            ref={tableRef}
          >
            <Table
              id={id}
              horizontalScroll={horizontalScroll}
              zebraTableBodyAsRow={zebra}
              aria-label={ariaLabel}
              aria-labelledby={ariaLabelledBy}
            >
              {!hideHeader && (
                <Table.Head>
                  <Table.Row>
                    {enableCheckbox && (
                      <Table.Cell
                        className='checkbox-cell'
                        width={CHECKBOX_CELL_WIDTH}
                        align='center'
                        onClick={_onCheckAll}
                      >
                        <Space direction='horizontal' justify='center'>
                          <Checkbox
                            inputProps={{ 'aria-label': 'Select all rows' }}
                            checked={isAllChecked}
                            disabled={
                              isCheckAllCheckboxDisabled || isCheckboxDisabled
                            }
                          />
                        </Space>
                      </Table.Cell>
                    )}

                    {renderHeaderColumns({
                      checklistMenu,
                      ...commonArgs,
                    })}
                  </Table.Row>
                </Table.Head>
              )}

              {renderHeaderBanner && (
                <Table.Body hoverable={false}>
                  <TableBanner type={headerBannerType}>
                    {renderHeaderBanner}
                  </TableBanner>
                </Table.Body>
              )}

              {showLoader && renderLoaderColumn(commonArgs)}

              {showEmptyState && (
                <Table.Body hoverable={false}>
                  <Table.Row>
                    <Table.Cell colSpan={MAX_COLUMN_COUNT}>
                      <EmptyState
                        context={emptyStateContext}
                        content={emptyStateContent}
                        header={emptyStateText}
                        small={emptyStateSize === 'small'}
                      />
                    </Table.Cell>
                  </Table.Row>
                </Table.Body>
              )}

              {showData &&
                data.map((rowData, rowIndex) => {
                  const commonRowArgs: CommonRowHelperArgs = {
                    ...commonArgs,
                    rowData,
                    rowIndex,
                  }

                  return (
                    <TableBodyAsRow
                      key={rowIndex}
                      hoverable={hoverable}
                      selected={
                        isRowSelected ? isRowSelected(rowData, rowIndex) : false
                      }
                      onClick={
                        onRowClick
                          ? () => onRowClick(rowData, rowIndex)
                          : () => undefined
                      }
                    >
                      <TableBodyAsRowFragment>
                        {enableCheckbox && (
                          <Table.Cell
                            className='checkbox-cell'
                            width={CHECKBOX_CELL_WIDTH}
                            onClick={(e) => {
                              e.stopPropagation()
                              _onItemCheck(rowData, rowIndex)
                            }}
                            align='center'
                          >
                            <Space direction='horizontal' justify='center'>
                              {renderRowCellCheckbox(rowData, rowIndex)}
                            </Space>
                          </Table.Cell>
                        )}

                        {renderBodyColumns(commonRowArgs)}
                      </TableBodyAsRowFragment>

                      {renderAdditionals(commonRowArgs)}
                    </TableBodyAsRow>
                  )
                })}
            </Table>
          </DataTableStyled>
        )
      }}
    </WithChecklistPopover>
  )
}

DataTable.displayName = 'DataTable'
